import React, { useMemo, useState } from "react";
import { Box, Paper, Typography, Skeleton, Chip } from "@mui/material";
import Chart from "react-apexcharts";
import TimelineIcon from "@mui/icons-material/Timeline";
import styles from "../style.module.css";

const VIEW_OPTIONS = [
  { key: "count", label: "Reservations" },
  { key: "amount", label: "Commission ($)" },
];

const COLORS = ["#10b981", "#ef4444", "#f59e0b"];

const buildSeries = (metrics, isAmountView) => {
  if (!metrics || metrics.length === 0) return [];
  if (isAmountView) {
    return [
      { name: "Total Commission", data: metrics.map((m) => parseFloat(m.totalCommissionAmount) || 0) },
      { name: "Confirmed Amt", data: metrics.map((m) => parseFloat(m.confirmedReservationAmount) || 0) },
      { name: "Cancelled Amt", data: metrics.map((m) => parseFloat(m.cancelledReservationAmount) || 0) },
    ];
  }
  return [
    { name: "Confirmed", data: metrics.map((m) => m.confirmedReservationCount || 0) },
    { name: "Cancelled", data: metrics.map((m) => m.cancelledReservationCount || 0) },
    { name: "No Show", data: metrics.map((m) => m.noshowReservationCount || 0) },
  ];
};

export const AffiliateLineChart = ({ title, metrics, isLoading }) => {
  const [selectedView, setSelectedView] = useState("count");
  const isAmountView = selectedView === "amount";

  const top5 = useMemo(() => (metrics || []).slice(0, 5), [metrics]);
  const hasData = top5.length > 0;
  const categories = top5.map((m) => m.affiliateName || `ID ${m.affiliateId}`);
  const series = useMemo(() => buildSeries(top5, isAmountView), [top5, isAmountView]);

  const options = {
    chart: {
      type: "line",
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      toolbar: { show: false },
      animations: { enabled: true, easing: "easeinout", speed: 600 },
      zoom: { enabled: false },
    },
    stroke: { curve: "smooth", width: 3 },
    markers: { size: 5, hover: { size: 7 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories,
      labels: {
        style: { fontSize: "11px", colors: "#64748b" },
        rotate: -30,
        rotateAlways: categories.length > 3,
      },
    },
    yaxis: {
      title: {
        text: isAmountView ? "Amount ($)" : "Reservations",
        style: { fontSize: "12px", color: "#64748b" },
      },
      labels: {
        style: { fontSize: "11px", colors: "#64748b" },
        formatter: (val) => isAmountView ? `$${Number(val).toLocaleString()}` : Math.round(val),
      },
      min: 0,
    },
    colors: COLORS,
    legend: { position: "top", fontSize: "12px", fontWeight: 600 },
    grid: { borderColor: "#f1f5f9", strokeDashArray: 3 },
    tooltip: {
      theme: "light",
      y: { formatter: (val) => isAmountView ? `$${Number(val).toLocaleString()}` : `${val} reservations` },
    },
  };

  return (
    <Paper className={styles.chartCard} elevation={0}>
      <Box className={styles.sectionHeader} sx={{ mt: 0 }}>
        <Typography className={styles.sectionTitle}>
          <TimelineIcon sx={{ fontSize: 18, mr: 0.5 }} />
          {title}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 2, justifyContent: "flex-end" }}>
        {VIEW_OPTIONS.map((opt) => (
          <Chip
            key={opt.key}
            label={opt.label}
            size="small"
            onClick={() => setSelectedView(opt.key)}
            sx={{
              fontWeight: 600, fontSize: "0.72rem", cursor: "pointer",
              ...(selectedView === opt.key
                ? { background: "#0f766e", color: "#fff", border: "1px solid #0f766e" }
                : { background: "rgba(15,118,110,0.08)", color: "#0f766e", border: "1px solid rgba(15,118,110,0.2)" }),
            }}
          />
        ))}
      </Box>

      {isLoading ? (
        <Box className={styles.chartSkeleton}>
          <Skeleton variant="rectangular" width="100%" height={320} />
        </Box>
      ) : hasData ? (
        <Chart options={options} series={series} type="line" height={340} />
      ) : (
        <Box
          className={styles.noData}
          sx={{ height: 340, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <Typography color="textSecondary">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default AffiliateLineChart;
